import React from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { Link } from "react-router-dom";
import { Trans } from "react-i18next";

import Button from "@/components/atoms/Buttons/Button";
import Input from "@/components/atoms/Input";
import Switch from "@/components/atoms/Switch";
import { FakeInput1xbet } from "@/components/molecules/Wrappers/FakeInput1xbet";
import { pixKeySchema } from "@/utils/validations";
import { formatDocumentMask } from "@/utils";
import i18n from "@/translate/i18n";
import config from "@/config";

import { usePaymentDataForm } from "./usePaymentDataForm";

function PaymentDataForm1xbet({ data, setData, setError, setIsFetching }) {
  const {
    customerDataValidated,
    isEditingCbu,
    setIsEditingCbu,
    isValidEmail,
    setIsValidEmail,
    emailHasErrors,
    documentInstructions,
    setDocumentInstructions,
    accountInstructions,
    setAccountInstructions,
    currentFormValues,
    setCurrentFormValues,
    initialAcceptedTerms,
    initialCountryIso,
    initialDocumentType,
    initialDocumentNumber,
    initialCbu,
    validateField,
    validEmail,
    handleEmailFocus,
    handleEmailBlur,
    handleFieldFocus,
    handleFieldBlur,
    handleFieldChange,
    handleSubmit,
    history,
    id,
    token,
  } = usePaymentDataForm({ data, setData, setError, setIsFetching });

  return (
    <Formik
      validationSchema={pixKeySchema}
      initialValues={{
        accepted_terms: initialAcceptedTerms,
        merchant_request_amount: data?.merchant_request_amount || "",
        bank_payment_data: data?.bank_payment_data || "",
        document_number: initialDocumentNumber,
        document_type: initialDocumentType,
        country_iso: initialCountryIso,
        email: data?.customer?.email || data?.customer_data?.email || "",
        account_id: initialCbu,
      }}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      {({
        errors,
        setErrors,
        setFieldTouched,
        setFieldError,
        setFieldValue,
        values,
      }) => {
        const newFormValues = {
          document_number: values.document_number,
          document_type: values.document_type,
          accepted_terms: values.accepted_terms,
          account_id: values.account_id,
        };
        if (
          JSON.stringify(currentFormValues) !== JSON.stringify(newFormValues)
        ) {
          setCurrentFormValues(newFormValues);
        }

        return (
          <Form
            onChange={() => setErrors({})}
            className="w-full flex flex-col flex-grow"
          >
            <div className="rounded-lg w-full overflow-hidden my-7 border-2 border-black border-opacity-10">
              <div className="flex flex-col justify-between items-center">
                {initialDocumentNumber ? (
                  <>
                    <FakeInput1xbet
                      label={i18n.t(
                        `form.document.${values?.document_type || "generic"}`,
                      )}
                      value={formatDocumentMask(
                        values?.document_number,
                        values?.document_type,
                      )}
                    />
                    <div className="pb-5 px-5 text-[0.65rem] text-left text-gray-500">
                      {documentInstructions.message}
                    </div>
                  </>
                ) : (
                  <div>
                    <Input
                      id="document_number"
                      name="document_number"
                      type="text"
                      label={i18n.t(
                        `form.document.${values?.document_type || "generic"}`,
                      )}
                      placeholder={i18n.t("form.documentPlaceholder")}
                      value={values?.document_number || ""}
                      error={!!errors?.document_number}
                      handleFocus={() =>
                        handleFieldFocus(
                          "document_number",
                          setFieldError,
                          setDocumentInstructions,
                        )
                      }
                      handleBlur={() =>
                        handleFieldBlur(
                          "document_number",
                          values?.document_number,
                          setFieldTouched,
                          setFieldError,
                          setDocumentInstructions,
                        )
                      }
                      handleChange={({ target }) =>
                        handleFieldChange(
                          "document_number",
                          target,
                          setFieldTouched,
                          setFieldValue,
                          setDocumentInstructions,
                          {
                            customLogic: (inputTarget, setFieldValueFn) => {
                              const documentType =
                                values?.document_type || "cuit-cuil";
                              const type = Array.isArray(documentType)
                                ? documentType[0] || "cuit-cuil"
                                : documentType;
                              setFieldValueFn(
                                "document_number",
                                formatDocumentMask(
                                  inputTarget.value,
                                  String(type).toLowerCase(),
                                ),
                              );
                            },
                          },
                        )
                      }
                      maxLength={11}
                      disabled={!!initialDocumentNumber}
                    />
                    {documentInstructions.show && (
                      <div
                        className={`mt-1 text-[0.65rem] pl-1 text-left ${
                          documentInstructions.type === "error"
                            ? "text-red-500"
                            : "text-gray-500"
                        }`}
                      >
                        {documentInstructions.message}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="w-full border-t border-black border-opacity-10" />
              <div>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  label={i18n.t("form.email")}
                  placeholder={i18n.t("form.emailPlaceholder")}
                  value={values?.email || ""}
                  error={!!errors?.email}
                  handleFocus={() => handleEmailFocus(setFieldError)}
                  handleChange={({ target }) => {
                    setFieldValue("email", target.value);
                    setIsValidEmail(validEmail(target.value));
                    if (emailHasErrors && target.value.trim() !== "")
                      setIsValidEmail(true);
                  }}
                  handleBlur={() =>
                    handleEmailBlur(
                      values?.email,
                      setFieldTouched,
                      setFieldError,
                    )
                  }
                />
                {emailHasErrors && (
                  <div className="mt-1 text-[0.65rem] text-red-500 pl-1 text-left">
                    {i18n.t("form.invalidEmail")}
                  </div>
                )}
              </div>
              <div className="w-full border-t border-black border-opacity-10" />
              {!isEditingCbu ? (
                <FakeInput1xbet
                  label={i18n.t("form.document.account_id")}
                  value={values?.account_id}
                  isEditable
                  onClick={() => setIsEditingCbu(true)}
                />
              ) : (
                <div>
                  <Input
                    id="account_id"
                    name="account_id"
                    type="text"
                    label={i18n.t("form.document.account_id")}
                    placeholder={i18n.t("form.accountPlaceholder")}
                    autoFocus={isEditingCbu}
                    value={values?.account_id || ""}
                    error={!!errors?.account_id}
                    handleFocus={() =>
                      handleFieldFocus(
                        "account_id",
                        setFieldError,
                        setAccountInstructions,
                        { showInfoOnFocus: false },
                      )
                    }
                    handleBlur={() =>
                      handleFieldBlur(
                        "account_id",
                        values?.account_id,
                        setFieldTouched,
                        setFieldError,
                        setAccountInstructions,
                        {
                          showInfoOnValid: false,
                          onValidCallback: () => setIsEditingCbu(false),
                        },
                      )
                    }
                    handleChange={({ target }) =>
                      handleFieldChange(
                        "account_id",
                        target,
                        setFieldTouched,
                        setFieldValue,
                        setAccountInstructions,
                        {
                          clearInstructionsOnChange: true,
                          customLogic: (inputTarget, setFieldValueFn) =>
                            setFieldValueFn("account_id", inputTarget.value),
                        },
                      )
                    }
                    maxLength={22}
                  />
                  {accountInstructions.show && (
                    <div
                      className={`mt-1 text-[0.65rem] pl-1 text-left ${
                        accountInstructions.type === "error"
                          ? "text-red-500"
                          : "text-gray-500"
                      }`}
                    >
                      {accountInstructions.message}
                    </div>
                  )}
                </div>
              )}

              {config.requireTerms && !initialAcceptedTerms && (
                <div
                  className="flex mt-5 items-center gap-3 bg-white"
                  data-theme="systemSwitchTheme"
                >
                  <Switch
                    isChecked={values?.accepted_terms}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFieldTouched("accepted_terms");
                      setFieldValue("accepted_terms", isChecked);
                      if (isChecked) {
                        const documentValidation = validateField(
                          values.document_number,
                          "document",
                        );
                        if (!documentValidation.isValid) {
                          setDocumentInstructions({
                            show: true,
                            message: documentValidation.message,
                            type: "error",
                          });
                          setFieldError(
                            "document_number",
                            documentValidation.message,
                          );
                        }
                        const accountValidation = validateField(
                          values.account_id,
                          "account",
                        );
                        if (!accountValidation.isValid) {
                          setAccountInstructions({
                            show: true,
                            message: accountValidation.message,
                            type: "error",
                          });
                          setFieldError(
                            "account_id",
                            accountValidation.message,
                          );
                        }
                      }
                    }}
                  />
                  <p className="m-0 text-left text-sm text-black">
                    <Trans
                      i18nKey="form.terms"
                      i18n={i18n}
                      components={[
                        <Link
                          className="text-blue-600 hover:text-blue-800 font-bold underline"
                          to={{
                            pathname: `/terms-and-conditions/${id}/${token}`,
                            search: history.location.search,
                          }}
                        />,
                        <Link
                          className="text-blue-600 hover:text-blue-800 font-bold underline"
                          to={{
                            pathname: `/privacy-policy/${id}/${token}`,
                            search: history.location.search,
                          }}
                        />,
                      ]}
                    />
                  </p>
                  {errors?.accepted_terms && (
                    <div className="text-red-500 text-xs absolute">
                      {errors.accepted_terms}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={!customerDataValidated || !isValidEmail}
            >
              {i18n.t("form.submit.withdrawal")}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
}

PaymentDataForm1xbet.propTypes = {
  setData: PropTypes.func,
  setError: PropTypes.func,
  setIsFetching: PropTypes.func,
  data: PropTypes.shape({
    customer: PropTypes.shape({
      country_iso: PropTypes.string,
      birth_date: PropTypes.string,
      terms_accepted_at: PropTypes.string,
      document_number: PropTypes.string,
      document_type: PropTypes.string,
      email: PropTypes.string,
    }),
    customer_data: PropTypes.shape({
      birth_date: PropTypes.string,
      document_number: PropTypes.string,
      email: PropTypes.string,
      name: PropTypes.string,
    }),
    customer_country_iso: PropTypes.string,
    customer_document_types: PropTypes.arrayOf(PropTypes.string),
    merchant_request_amount: PropTypes.number,
    merchant_request_currency_iso: PropTypes.string,
    type: PropTypes.string,
    bank_payment_data: PropTypes.shape({
      account_id: PropTypes.string,
      allowReview: PropTypes.bool,
    }),
  }),
};

export default PaymentDataForm1xbet;
